import { useLocation } from "@reach/router";
import { useEffect, useRef } from "react";

/**
 * A React hook that runs a callback whenever the route's pathname changes.
 *
 * This hook uses `@reach/router`'s `useLocation()` internally to track
 * navigation changes. It provides control over whether the callback
 * should run on the first render or only on subsequent navigations.
 *
 * @template {Function} T
 * @param {(pathname: string) => void} callback - The function to run when the pathname changes.
 * @param {boolean} [skipInitial=true] - Whether to skip running the callback on the initial render.
 *
 * @example
 * // Log when the user navigates to a new route
 * useOnNavigate((path) => {
 *   console.log("Navigated to:", path);
 * });
 *
 * @example
 * // Run on mount *and* on navigation
 * useOnNavigate((path) => {
 *   fetchDataForRoute(path);
 * }, false);
 *
 * @remarks
 * - Uses `useRef` to track the previous pathname and detect when it changes.
 * - By default (`skipInitial = true`), the callback does not run on the first mount.
 *   Set `skipInitial = false` if you want it to run immediately on mount as well.
 */
export const useOnNavigate = (cb: (pathname: string) => void, skipInitial = true) => {
  const { pathname } = useLocation();
  const previousPath = useRef(pathname);
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (skipInitial && isFirstRun.current) {
      isFirstRun.current = false;
      previousPath.current = pathname;

      return;
    }

    if (previousPath.current !== pathname) {
      cb(pathname);
      previousPath.current = pathname;
    }
  }, [pathname, cb, skipInitial]);
};
